import { useState, type ReactNode } from "react";
import { AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  PAYOUT_STATUS_LABELS,
  formatIban,
  type CurrencyCode,
  type Payout,
  type PayoutBalance,
  type PayoutBankAccount,
  type PayoutSummary,
} from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { QueryError } from "@/components/query-error";
import {
  useCreateAdjustmentMutation,
  useCreatePayoutMutation,
  useEditPayoutMutation,
  useIsSuperAdmin,
  useMarkPayoutPaidMutation,
  useOrganizerFinanceQuery,
  useRejectPayoutMutation,
  useUpdateBankAccountMutation,
} from "@/features/api";
import { ApiError, showApiError } from "@/lib/api-client";
import { dateOnly, money, parseAmount, payoutStatusVariant, todayIso } from "@/lib/format";

type DialogState =
  | { kind: "bank" }
  | { kind: "create" }
  | { kind: "adjust" }
  | { kind: "edit"; payout: Payout }
  | { kind: "paid"; payout: Payout }
  | { kind: "reject"; payout: Payout }
  | null;

/**
 * Organizatörün cari hesabı: bakiye, ödemeler, düzeltmeler, etkinlik kazançları.
 * Bakiye ve kurallar backend'de (`OrganizerFinance`); burada sadece gösterim ve
 * SuperAdmin aksiyonları var.
 */
export function OrganizerFinanceCard({
  organizerId,
  legalName,
}: {
  organizerId: string;
  legalName: string | null;
}) {
  const isSuper = useIsSuperAdmin();
  const { data, isLoading, error, refetch, isRefetching } = useOrganizerFinanceQuery(organizerId);
  const [dialog, setDialog] = useState<DialogState>(null);
  const close = () => setDialog(null);

  return (
    <Card>
      <CardHeader className="max-sm:has-data-[slot=card-action]:grid-cols-1">
        <CardTitle>Cari</CardTitle>
        <CardDescription>
          Etkinlik bitince çekilebilir olur. Organizatör payı iade oranında düşer; iadede bankanın
          geri vermediği kart komisyonu TicketNjoy'da kalır.
        </CardDescription>
        {isSuper && data && (
          <CardAction className="flex flex-wrap gap-2 max-sm:col-start-1 max-sm:row-span-1 max-sm:row-start-3 max-sm:justify-self-start">
            <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "adjust" })}>
              Düzeltme ekle
            </Button>
            <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
              Ödeme oluştur
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Yükleniyor...</p>
        ) : error || !data ? (
          <QueryError message="Cari yüklenemedi." onRetry={() => refetch()} retrying={isRefetching} />
        ) : (
          <FinanceBody data={data} legalName={legalName} isSuper={isSuper} onAction={setDialog} />
        )}
      </CardContent>

      {data && dialog?.kind === "bank" && (
        <BankAccountDialog organizerId={organizerId} bank={data.bankAccount} onClose={close} />
      )}
      {data && dialog?.kind === "create" && (
        <CreatePayoutDialog organizerId={organizerId} data={data} onClose={close} />
      )}
      {data && dialog?.kind === "adjust" && (
        <AdjustmentDialog organizerId={organizerId} data={data} onClose={close} />
      )}
      {data && dialog?.kind === "edit" && (
        <EditPayoutDialog
          organizerId={organizerId}
          payout={dialog.payout}
          available={availableFor(data, dialog.payout)}
          onClose={close}
          onReject={() => setDialog({ kind: "reject", payout: dialog.payout })}
        />
      )}
      {dialog?.kind === "paid" && (
        <MarkPaidDialog organizerId={organizerId} payout={dialog.payout} onClose={close} />
      )}
      {dialog?.kind === "reject" && (
        <RejectDialog organizerId={organizerId} payout={dialog.payout} onClose={close} />
      )}
    </Card>
  );
}

/** Açık kaydın kendi ayırdığı tutar bakiyeye geri eklenir (backend ile aynı kural). */
function availableFor(data: PayoutSummary, payout: Payout): number {
  const balance = data.balances.find((b) => b.currency === payout.currency)?.available ?? 0;
  return balance + payout.amount;
}

function FinanceBody({
  data,
  legalName,
  isSuper,
  onAction,
}: {
  data: PayoutSummary;
  legalName: string | null;
  isSuper: boolean;
  onAction: (d: DialogState) => void;
}) {
  return (
    <>
      <BalanceTable balances={data.balances} />
      <BankRow
        bank={data.bankAccount}
        legalName={legalName}
        onEdit={isSuper ? () => onAction({ kind: "bank" }) : undefined}
      />

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Ödemeler</h3>
        {data.payouts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Henüz ödeme yok.</p>
        ) : (
          <ul className="divide-y rounded-md border text-sm">
            {data.payouts.map((p) => (
              <PayoutRow key={p.id} payout={p} isSuper={isSuper} onAction={onAction} />
            ))}
          </ul>
        )}
      </section>

      {data.adjustments.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Düzeltmeler</h3>
          <ul className="divide-y rounded-md border text-sm">
            {data.adjustments.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-4 px-3 py-2">
                <div>
                  <div>{a.reason}</div>
                  <div className="text-xs text-muted-foreground">{dateOnly(a.createdAt)}</div>
                </div>
                <span className={a.amount < 0 ? "tabular-nums text-destructive" : "tabular-nums"}>
                  {a.amount > 0 ? "+" : ""}
                  {money(a.amount, a.currency)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.events.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Etkinlik bazında net</h3>
          <ul className="divide-y rounded-md border text-sm">
            {data.events.map((e) => (
              <li key={`${e.eventId}-${e.currency}`} className="flex items-center justify-between gap-4 px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground">
                    Bitiş {dateOnly(e.endsAt)} · {e.orderCount} sipariş
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tabular-nums">{money(e.net, e.currency)}</span>
                  <Badge variant={e.settled ? "success" : "secondary"}>
                    {e.settled ? "Bitti" : "Bitmedi"}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function BalanceTable({ balances }: { balances: PayoutBalance[] }) {
  if (balances.length === 0) {
    return <p className="text-sm text-muted-foreground">Henüz satış veya ödeme yok.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Para birimi</th>
            <th className="px-3 py-2 text-right font-medium">Bekleyen</th>
            <th className="px-3 py-2 text-right font-medium">Biten etkinlik</th>
            <th className="px-3 py-2 text-right font-medium">Düzeltme</th>
            <th className="px-3 py-2 text-right font-medium">Ödenen</th>
            <th className="px-3 py-2 text-right font-medium">Süreçte</th>
            <th className="py-2 pl-3 text-right font-medium">Çekilebilir</th>
          </tr>
        </thead>
        <tbody>
          {balances.map((b) => (
            <tr key={b.currency} className="border-b last:border-0">
              <td className="py-2 pr-3 font-medium">{b.currency}</td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{money(b.pendingEarnings, b.currency)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(b.settledEarnings, b.currency)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(b.adjustments, b.currency)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(b.paidOut, b.currency)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{money(b.inProgress, b.currency)}</td>
              <td className={`py-2 pl-3 text-right font-semibold tabular-nums ${b.available < 0 ? "text-destructive" : ""}`}>
                {money(b.available, b.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (s: string) => s.trim().toLocaleLowerCase("tr-TR").replace(/\s+/g, " ");
  return !!a && !!b && norm(a) === norm(b);
}

function BankRow({
  bank,
  legalName,
  onEdit,
}: {
  bank: PayoutBankAccount;
  legalName: string | null;
  onEdit?: () => void;
}) {
  if (!bank.iban) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-400">
        <span className="flex items-center gap-2">
          <AlertTriangle className="size-4" /> Organizatör banka hesabı girmemiş.
        </span>
        {onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            Ekle
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border p-3 text-sm">
      <div className="min-w-0 space-y-1">
        <div className="text-xs text-muted-foreground">Kayıtlı banka hesabı</div>
        <div className="font-mono tabular-nums break-all">{formatIban(bank.iban)}</div>
        <div>{bank.ibanOwnerName}</div>
        {legalName && !sameName(bank.ibanOwnerName, legalName) && (
          <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-3.5" /> Hesap sahibi fatura ünvanından ({legalName}) farklı.
          </div>
        )}
      </div>
      {onEdit && (
        <Button size="sm" variant="outline" onClick={onEdit}>
          Değiştir
        </Button>
      )}
    </div>
  );
}

function BankAccountDialog({
  organizerId,
  bank,
  onClose,
}: {
  organizerId: string;
  bank: PayoutBankAccount;
  onClose: () => void;
}) {
  const update = useUpdateBankAccountMutation(organizerId);
  const [iban, setIban] = useState(formatIban(bank.iban));
  const [owner, setOwner] = useState(bank.ibanOwnerName ?? "");
  const normalized = iban.replace(/\s+/g, "").toUpperCase();
  const valid = isValidIban(iban) && owner.trim().length >= 3;
  const dirty = normalized !== (bank.iban ?? "") || owner.trim() !== (bank.ibanOwnerName ?? "");

  async function onSubmit() {
    if (!valid || !dirty) return;
    try {
      await update.mutateAsync({ iban: normalized, ibanOwnerName: owner.trim() });
      toast.success("Banka hesabı güncellendi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title={bank.iban ? "Banka hesabını değiştir" : "Banka hesabı ekle"}
          description="Organizatöre e-posta gitmez; değişiklik Aktivite'sinde TicketNjoy adıyla, IBAN maskeli görünür. Onay bekleyen ödeme talepleri yeni hesaba geçer, onaylanmışlar eski hesapta kalır."
        />
        <div className="space-y-4">
          <Field label="IBAN" htmlFor="ba-iban">
            <Input
              id="ba-iban"
              className="font-mono"
              placeholder="TR00 0000 0000 0000 0000 0000 00"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
            />
            {iban.trim() !== "" && !isValidIban(iban) && (
              <p className="text-xs text-destructive">TR ile başlayan 26 karakterlik IBAN girin.</p>
            )}
          </Field>
          <Field label="Hesap sahibi" htmlFor="ba-owner">
            <Input id="ba-owner" maxLength={200} value={owner} onChange={(e) => setOwner(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={!valid || !dirty || update.isPending}>
            {update.isPending ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayoutRow({
  payout: p,
  isSuper,
  onAction,
}: {
  payout: Payout;
  isSuper: boolean;
  onAction: (d: DialogState) => void;
}) {
  return (
    <li className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium tabular-nums">{money(p.amount, p.currency)}</span>
          <Badge variant={payoutStatusVariant(p.status)}>{PAYOUT_STATUS_LABELS[p.status]}</Badge>
          <span className="text-xs text-muted-foreground">
            {p.source === "Admin" ? "Admin oluşturdu" : "Organizatör talebi"} · {dateOnly(p.createdAt)}
          </span>
        </div>
        <div className="font-mono text-xs text-muted-foreground">
          {formatIban(p.iban)} · {p.ibanOwnerName}
        </div>
        {p.status === "Paid" && (
          <div className="text-xs text-muted-foreground">
            Gönderildi {dateOnly(p.paidAt)} · Dekont {p.bankReference}
          </div>
        )}
        {p.note && <div className="text-xs">Not: {p.note}</div>}
        {p.rejectReason && <div className="text-xs text-destructive">Ret: {p.rejectReason}</div>}
      </div>
      {isSuper && p.status === "Requested" && (
        <Button size="sm" onClick={() => onAction({ kind: "edit", payout: p })}>
          İncele
        </Button>
      )}
      {isSuper && p.status === "Approved" && (
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={() => onAction({ kind: "edit", payout: p })}>
            Düzenle
          </Button>
          <Button size="sm" onClick={() => onAction({ kind: "paid", payout: p })}>
            Ödendi işaretle
          </Button>
        </div>
      )}
    </li>
  );
}

/* ─────────────────────────── Dialog'lar ─────────────────────────── */

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function isValidIban(raw: string): boolean {
  return /^TR\d{24}$/.test(raw.replace(/\s+/g, "").toUpperCase());
}

function OverBalanceNote({ amount, available, currency }: { amount: number | null; available: number; currency: CurrencyCode }) {
  if (amount === null || amount <= available) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>
        Çekilebilir bakiye {money(available, currency)}. Bu tutar bakiyeyi aşıyor (avans); bakiye eksiye
        düşer ve sonraki kazançlardan kapanır. Not zorunlu, organizatör görür.
      </span>
    </div>
  );
}

function CreatePayoutDialog({
  organizerId,
  data,
  onClose,
}: {
  organizerId: string;
  data: PayoutSummary;
  onClose: () => void;
}) {
  const create = useCreatePayoutMutation(organizerId);
  const currencies = data.balances.length > 0 ? data.balances.map((b) => b.currency) : (["TRY"] as CurrencyCode[]);
  const [currency, setCurrency] = useState<CurrencyCode>(currencies[0]);
  const available = data.balances.find((b) => b.currency === currency)?.available ?? 0;
  const [amount, setAmount] = useState(available > 0 ? available.toFixed(2).replace(".", ",") : "");
  const [status, setStatus] = useState<"Approved" | "Paid">("Approved");
  const [iban, setIban] = useState(formatIban(data.bankAccount.iban));
  const [owner, setOwner] = useState(data.bankAccount.ibanOwnerName ?? "");
  const [note, setNote] = useState("");
  const [paidOn, setPaidOn] = useState(todayIso());
  const [bankReference, setBankReference] = useState("");

  const amountValue = parseAmount(amount);
  const over = amountValue !== null && amountValue > available;
  const valid =
    amountValue !== null &&
    amountValue > 0 &&
    isValidIban(iban) &&
    owner.trim().length >= 3 &&
    (!over || note.trim().length > 0) &&
    (status === "Approved" || (!!paidOn && bankReference.trim().length >= 2));

  async function onSubmit() {
    if (!valid || amountValue === null) return;
    try {
      await create.mutateAsync({
        currency,
        amount: amountValue,
        status,
        iban: iban.replace(/\s+/g, ""),
        ibanOwnerName: owner.trim(),
        note: note.trim() || undefined,
        paidOn: status === "Paid" ? paidOn : undefined,
        bankReference: status === "Paid" ? bankReference.trim() : undefined,
      });
      toast.success(status === "Paid" ? "Ödeme kaydedildi, organizatöre bildirildi." : "Ödeme onaylandı olarak oluşturuldu.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title="Ödeme oluştur"
          description="Organizatör talep etmeden ödeme. Organizatöre e-posta gider ve Finans sayfasında görür."
        />
        <div className="space-y-4">
          {currencies.length > 1 && (
            <Field label="Para birimi" htmlFor="pc-currency">
              <Select value={currency} onValueChange={(v) => setCurrency(v as CurrencyCode)}>
                <SelectTrigger id="pc-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={`Tutar (${currency}) — çekilebilir ${money(available, currency)}`} htmlFor="pc-amount">
            <Input id="pc-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <OverBalanceNote amount={amountValue} available={available} currency={currency} />
          <Field label="Durum" htmlFor="pc-status">
            <Select value={status} onValueChange={(v) => setStatus(v as "Approved" | "Paid")}>
              <SelectTrigger id="pc-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Approved">Onaylandı — EFT'yi sonra yapacağım</SelectItem>
                <SelectItem value="Paid">Ödendi — EFT yapıldı</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {status === "Paid" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ödeme tarihi" htmlFor="pc-paid-on">
                <Input id="pc-paid-on" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
              </Field>
              <Field label="Dekont / referans no" htmlFor="pc-ref">
                <Input id="pc-ref" value={bankReference} onChange={(e) => setBankReference(e.target.value)} />
              </Field>
            </div>
          )}
          <Field label="IBAN" htmlFor="pc-iban">
            <Input id="pc-iban" className="font-mono" value={iban} onChange={(e) => setIban(e.target.value)} />
          </Field>
          <Field label="Hesap sahibi" htmlFor="pc-owner">
            <Input id="pc-owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
          </Field>
          <Field label={over ? "Not (zorunlu)" : "Not (organizatör görür)"} htmlFor="pc-note">
            <Textarea id="pc-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={!valid || create.isPending}>
            {create.isPending ? "Kaydediliyor..." : "Oluştur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPayoutDialog({
  organizerId,
  payout,
  available,
  onClose,
  onReject,
}: {
  organizerId: string;
  payout: Payout;
  available: number;
  onClose: () => void;
  onReject: () => void;
}) {
  const edit = useEditPayoutMutation(organizerId);
  const review = payout.status === "Requested";
  const [amount, setAmount] = useState(payout.amount.toFixed(2).replace(".", ","));
  const [iban, setIban] = useState(formatIban(payout.iban));
  const [owner, setOwner] = useState(payout.ibanOwnerName);
  const [note, setNote] = useState(payout.note ?? "");

  const amountValue = parseAmount(amount);
  const over = amountValue !== null && amountValue > available;
  const valid =
    amountValue !== null &&
    amountValue > 0 &&
    isValidIban(iban) &&
    owner.trim().length >= 3 &&
    (!over || note.trim().length > 0);

  async function submit(approve: boolean) {
    if (!valid || amountValue === null) return;
    try {
      await edit.mutateAsync({
        id: payout.id,
        approve,
        body: {
          amount: amountValue,
          iban: iban.replace(/\s+/g, ""),
          ibanOwnerName: owner.trim(),
          note: note.trim() || undefined,
        },
      });
      toast.success(approve ? "Ödeme onaylandı, organizatöre bildirildi." : "Değişiklikler kaydedildi.");
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(err.detail);
        onClose();
        return;
      }
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title={review ? "Ödeme talebini incele" : "Onaylanmış ödemeyi düzenle"}
          description={
            review
              ? "Tutarı, IBAN'ı veya notu değiştirip onaylayabilirsin. Onayda organizatöre e-posta gider."
              : "EFT öncesi düzeltme. Değişiklik organizatörün denetim kaydında görünür."
          }
        />
        <div className="space-y-4">
          <Field label={`Tutar (${payout.currency}) — çekilebilir ${money(available, payout.currency)}`} htmlFor="ep-amount">
            <Input id="ep-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <OverBalanceNote amount={amountValue} available={available} currency={payout.currency} />
          <Field label="IBAN" htmlFor="ep-iban">
            <Input id="ep-iban" className="font-mono" value={iban} onChange={(e) => setIban(e.target.value)} />
          </Field>
          <Field label="Hesap sahibi" htmlFor="ep-owner">
            <Input id="ep-owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
          </Field>
          <Field label={over ? "Not (zorunlu)" : "Not (organizatör görür)"} htmlFor="ep-note">
            <Textarea id="ep-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <DialogFooter className="sm:justify-between">
          <Button variant="outline" className="text-destructive" onClick={onReject} disabled={edit.isPending}>
            Reddet
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => submit(false)} disabled={!valid || edit.isPending}>
              Kaydet
            </Button>
            {review && (
              <Button onClick={() => submit(true)} disabled={!valid || edit.isPending}>
                {edit.isPending ? "Kaydediliyor..." : "Onayla"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({
  organizerId,
  payout,
  onClose,
}: {
  organizerId: string;
  payout: Payout;
  onClose: () => void;
}) {
  const markPaid = useMarkPayoutPaidMutation(organizerId);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [bankReference, setBankReference] = useState("");
  const valid = !!paidOn && bankReference.trim().length >= 2;

  async function onSubmit() {
    if (!valid) return;
    try {
      await markPaid.mutateAsync({ id: payout.id, body: { paidOn, bankReference: bankReference.trim() } });
      toast.success("Ödendi olarak işaretlendi, organizatöre bildirildi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  async function copyIban() {
    try {
      await navigator.clipboard.writeText(payout.iban);
      toast.success("IBAN kopyalandı.");
    } catch {
      toast.error("Kopyalanamadı.");
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader title="Ödendi işaretle" description="EFT'yi yaptıktan sonra tarih ve dekont numarasını gir." />
        <div className="space-y-4">
          <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-sm">
            <div className="text-lg font-semibold tabular-nums">{money(payout.amount, payout.currency)}</div>
            <div className="flex items-center gap-2">
              <span className="font-mono tabular-nums">{formatIban(payout.iban)}</span>
              <Button type="button" variant="ghost" size="icon-xs" onClick={copyIban} aria-label="IBAN'ı kopyala">
                <Copy />
              </Button>
            </div>
            <div className="text-muted-foreground">{payout.ibanOwnerName}</div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ödeme tarihi" htmlFor="mp-paid-on">
              <Input id="mp-paid-on" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
            </Field>
            <Field label="Dekont / referans no" htmlFor="mp-ref">
              <Input id="mp-ref" value={bankReference} onChange={(e) => setBankReference(e.target.value)} />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={!valid || markPaid.isPending}>
            {markPaid.isPending ? "Kaydediliyor..." : "Ödendi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({
  organizerId,
  payout,
  onClose,
}: {
  organizerId: string;
  payout: Payout;
  onClose: () => void;
}) {
  const reject = useRejectPayoutMutation(organizerId);
  const [reason, setReason] = useState("");

  async function onSubmit() {
    try {
      await reject.mutateAsync({ id: payout.id, body: { reason: reason.trim() } });
      toast.success("Talep reddedildi, organizatöre bildirildi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title={`${money(payout.amount, payout.currency)} reddedilsin mi?`}
          description="Tutar organizatörün çekilebilir bakiyesine geri döner. Sebep organizatöre e-postayla gider."
        />
        <Field label="Sebep" htmlFor="rj-reason">
          <Textarea id="rj-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button variant="destructive" onClick={onSubmit} disabled={reason.trim().length < 3 || reject.isPending}>
            {reject.isPending ? "Reddediliyor..." : "Reddet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentDialog({
  organizerId,
  data,
  onClose,
}: {
  organizerId: string;
  data: PayoutSummary;
  onClose: () => void;
}) {
  const create = useCreateAdjustmentMutation(organizerId);
  const currencies = data.balances.length > 0 ? data.balances.map((b) => b.currency) : (["TRY"] as CurrencyCode[]);
  const [currency, setCurrency] = useState<CurrencyCode>(currencies[0]);
  const [direction, setDirection] = useState<"credit" | "debit">("debit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const amountValue = parseAmount(amount);
  const valid = amountValue !== null && amountValue > 0 && reason.trim().length >= 3;

  async function onSubmit() {
    if (!valid || amountValue === null) return;
    try {
      await create.mutateAsync({
        currency,
        amount: direction === "credit" ? amountValue : -amountValue,
        reason: reason.trim(),
      });
      toast.success("Düzeltme eklendi.");
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title="Bakiye düzeltmesi"
          description="Chargeback, elden ödeme veya hatalı EFT gibi durumlar için. Silinemez; geri almak için ters düzeltme ekle. Organizatör görür."
        />
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Yön" htmlFor="adj-direction">
              <Select value={direction} onValueChange={(v) => setDirection(v as "credit" | "debit")}>
                <SelectTrigger id="adj-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Bakiyeden düş (−)</SelectItem>
                  <SelectItem value="credit">Bakiyeye ekle (+)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {currencies.length > 1 ? (
              <Field label="Para birimi" htmlFor="adj-currency">
                <Select value={currency} onValueChange={(v) => setCurrency(v as CurrencyCode)}>
                  <SelectTrigger id="adj-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : null}
          </div>
          <Field label={`Tutar (${currency})`} htmlFor="adj-amount">
            <Input id="adj-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Sebep" htmlFor="adj-reason">
            <Textarea id="adj-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={onSubmit} disabled={!valid || create.isPending}>
            {create.isPending ? "Kaydediliyor..." : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
